import { captureRemixErrorBoundaryError, withSentry } from '@sentry/remix';
import { useStore } from '@nanostores/react';
import type { LinksFunction, LoaderFunction } from '@remix-run/cloudflare';
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  redirect,
  Scripts,
  ScrollRestoration,
  useRouteError,
  type ClientLoaderFunctionArgs,
  useLocation,
} from '@remix-run/react';
import tailwindReset from '@unocss/reset/tailwind-compat.css?url';
import { themeStore } from './lib/stores/theme';
import { stripIndents } from './utils/stripIndent';
import { createHead } from 'remix-island';
import { useEffect, useState, Suspense } from 'react';
import { ToastContainer } from 'react-toastify';
import reactToastifyStyles from 'react-toastify/dist/ReactToastify.css?url';
import globalStyles from './styles/index.scss?url';
import xtermStyles from '@xterm/xterm/css/xterm.css?url';
import themeVariables from './styles/theme-variables.css?url';

// import independenceDayStyles from './styles/independence-day.css?url';
import { Header } from '~/components/header/Header.client';
import 'virtual:uno.css';
import { ClientOnly } from 'remix-utils/client-only';
import { json, defer } from '@remix-run/cloudflare';
import { LazyBackgroundImage } from '~/components/ui/LazyImage';
import * as Sentry from '@sentry/remix';
import { userStore } from './lib/stores/user';
import { useLoaderData, Await } from '@remix-run/react';
import { checkAuth } from './services/auth.server';
import { Ribbon } from './components/ui/Ribbon';
import { Ribbon2 } from './components/ui/Ribbon2';
import { SafariBlockModal } from './components/ui/SafariBlockModal';
import { WelcomeModal } from './components/ui/WelcomeModal';
import { SupabaseAuthSuccessModal } from './components/supabase/SupabaseAuthSuccessModal';

// import { UnsplashAttribution } from './components/ui/UnsplashAttribution';
// import { Hash } from 'lucide-react';
import usePosthogPageView from './lib/hooks/posthog/usePosthogPageView';
import { usePosthogIdentify } from './lib/hooks/posthog/usePosthogIdentify';
import { usePosthogUserProperties } from './lib/hooks/posthog/usePosthogUserProperties';
import { LanguageSwitcher } from './components/ui/LanguageSwitcher';
import { me } from './services/userService.server';
import { type UserData } from './services/interfaceService';
import { getDb } from './services/db.server';

// cloudRevert
// import { EventStatusProvider } from './components/ui/EventStatusProvider';
import { getCurrencyFromCountry } from './utils/currency';
import { ProUpgradeModal } from '~/components/ui/ProUpgradeModal';
import { Custom404 } from '~/components/ui/Custom404';
import { QuestionnaireModal } from '~/components/modals/QuestionnaireModal';

// Type definition for server loader data
interface ServerLoaderData {
  baseUrl?: string;
  newDomain?: string;
  paymentUrl?: string;
  token?: string;
  accessToken?: string;
  user?: {
    id: string;
    email: string;
    name?: string;
    provider: 'github' | 'google' | 'otp';
    picture: string;
  };
  prompt?: string;
  ENV: {
    SENTRY_DSN: string;
    POSTHOG_KEY: string;
    POSTHOG_HOST: string;
    EARLY_ACCESS_URL: string;
    PAYMENT_URL: string;
    POSTHOG_USE_PROXY: string;
    VITE_QUESTIONNAIRE_SHEET_ID: string;
    VITE_QUESTIONNAIRE_TAB_ID: string;
    VITE_QUESTIONNAIRE_USERNAME: string;
  };
  userData: UserData | null;

  // cloudRevert (burst)
  // projects: CloudChatHistoryItem[] | Promise<CloudChatHistoryItem[]> /* Deferred projects */;
}

// List of public routes that don't require authentication
const publicRoutes = [
  '/onboarding',
  '/pricing',
  '/auth/google',
  '/auth/google/callback',
  '/setup',
  '/check-activation',
  '/maintenance',
];

export const loader: LoaderFunction = async ({ request, context }) => {
  /* console.log('[Root Loader] === Starting Root Loader ==='); */

  const url = new URL(request.url);

  /* console.log('[Root Loader] URL:', url.toString()); */

  const isPublicRoute = publicRoutes.some((route) => url.pathname.startsWith(route));

  // Always load environment variables, regardless of route
  const ENV = {
    POSTHOG_KEY: process.env.POSTHOG_KEY || context.cloudflare?.env?.POSTHOG_KEY || '',
    POSTHOG_HOST: process.env.POSTHOG_HOST || context.cloudflare?.env?.POSTHOG_HOST || '',
    POSTHOG_USE_PROXY: process.env.POSTHOG_USE_PROXY || context.cloudflare?.env?.POSTHOG_USE_PROXY,
    SENTRY_DSN: process.env.SENTRY_DSN || context.cloudflare?.env?.SENTRY_DSN || '',
    EARLY_ACCESS_URL:
      process.env.NEXT_PUBLIC_GET_ACCESS_URL_DEV ||
      process.env.NEXT_PUBLIC_GET_ACCESS_URL ||
      context.cloudflare?.env?.NEXT_PUBLIC_GET_ACCESS_URL ||
      '',
    PAYMENT_URL: process.env.VITE_PUBLIC_PAYMENT_URL || context.cloudflare?.env?.VITE_PUBLIC_PAYMENT_URL || '',
    VITE_QUESTIONNAIRE_SHEET_ID:
      process.env.VITE_QUESTIONNAIRE_SHEET_ID || context.cloudflare?.env?.VITE_QUESTIONNAIRE_SHEET_ID || '',
    VITE_QUESTIONNAIRE_TAB_ID:
      process.env.VITE_QUESTIONNAIRE_TAB_ID || context.cloudflare?.env?.VITE_QUESTIONNAIRE_TAB_ID || '',
    VITE_QUESTIONNAIRE_USERNAME:
      process.env.VITE_QUESTIONNAIRE_USERNAME || context.cloudflare?.env?.VITE_QUESTIONNAIRE_USERNAME || '',
  };

  // Debug logging for PostHog environment variables
  console.log('[Root Loader] ===== PostHog Environment Debug =====');
  console.log('[Root Loader] process.env.POSTHOG_USE_PROXY:', process.env.POSTHOG_USE_PROXY);
  console.log('[Root Loader] context.cloudflare?.env?.POSTHOG_USE_PROXY:', context.cloudflare?.env?.POSTHOG_USE_PROXY);
  console.log('[Root Loader] Final ENV.POSTHOG_USE_PROXY:', ENV.POSTHOG_USE_PROXY);
  console.log('[Root Loader] =======================================');

  /* Skip auth check for public routes */
  if (isPublicRoute) {
    return json({
      ENV,
      paymentUrl: ENV.PAYMENT_URL,
    });
  }

  const DATABASE_URL = process.env.DATABASE_URL || context.cloudflare.env.DATABASE_URL;
  const baseUrl =
    process.env.NEXT_PUBLIC_GET_ACCESS_URL_DEV ||
    process.env.NEXT_PUBLIC_GET_ACCESS_URL ||
    context.cloudflare.env.NEXT_PUBLIC_GET_ACCESS_URL;
  const sentryDsn = process.env.SENTRY_DSN || context.cloudflare.env.SENTRY_DSN;
  const paymentUrl = process.env.VITE_PUBLIC_PAYMENT_URL || context.cloudflare.env.VITE_PUBLIC_PAYMENT_URL;
  const posthogKey = process.env.POSTHOG_KEY || context.cloudflare.env.POSTHOG_KEY;
  const posthogHost = process.env.POSTHOG_HOST || context.cloudflare.env.POSTHOG_HOST;
  const posthogUseProxy = process.env.POSTHOG_USE_PROXY || context.cloudflare.env.POSTHOG_USE_PROXY;
  const oldDomain = process.env.OLD_DOMAIN || context.cloudflare.env.OLD_DOMAIN;
  const newDomain = process.env.NEW_DOMAIN || context.cloudflare.env.NEW_DOMAIN;
  const questionnaireSheetId =
    process.env.VITE_QUESTIONNAIRE_SHEET_ID || context.cloudflare.env.VITE_QUESTIONNAIRE_SHEET_ID;
  const questionnaireTabId = process.env.VITE_QUESTIONNAIRE_TAB_ID || context.cloudflare.env.VITE_QUESTIONNAIRE_TAB_ID;
  const questionnaireUsername =
    process.env.VITE_QUESTIONNAIRE_USERNAME || context.cloudflare.env.VITE_QUESTIONNAIRE_USERNAME;

  /*
   * console.log('[Root Loader] Base URL:', baseUrl ? 'Present' : 'Missing');
   * console.log('[Root Loader] Sentry DSN:', sentryDsn ? 'Present' : 'Missing');
   */

  const checkAuthResponse = await checkAuth(request);

  console.log('[Root Loader] Check auth response:', checkAuthResponse ? 'Present' : 'Not found');

  if (checkAuthResponse) {
    /*
     * console.log('[Root Loader] User provider:', checkAuthResponse.provider);
     * console.log('[Root Loader] User email:', checkAuthResponse.email);
     * console.log('[Root Loader] Access token present:', !!checkAuthResponse.accessToken);
     * console.log('[Root Loader] External token present:', !!checkAuthResponse.token);
     */

    /* For both OTP and Google users, we need both tokens */
    if (checkAuthResponse.accessToken && checkAuthResponse.token) {
      /* console.log('[Root Loader] Authentication successful, returning user data'); */

      const requestOrigin = request.headers.get('origin') || url.origin;

      /*
       * if (requestOrigin === oldDomain && url.pathname !== '/migrate') {
       *   console.log('[Root Loader] Forcing redirect to /migrate for alpha-v1-dualite.pages.dev');
       *   return redirect('/migrate');
       * }
       */

      let userData;

      // cloudRevert (burst)
      /* Defer projects to avoid blocking initial render */
      // let projects: Promise<CloudChatHistoryItem[]> | CloudChatHistoryItem[] = [];

      try {
        const db = getDb(DATABASE_URL);
        userData = await me(db, checkAuthResponse.token);

        if (!userData || !userData.email) {
          console.error('[Root Loader] User data not found');
          return redirect('/onboarding');
        }

        console.log('[Root Loader] User data:', userData, checkAuthResponse);

        // cloudRevert (burst)
        // Prepare deferred fetch for projects to speed up first paint
        // if (userData && checkAuthResponse.token) {
        //   projects = (async () => {
        //     try {
        //       const rawProjects = await getProjectsWithConnections(db, checkAuthResponse.token);

        //       const serialized: CloudChatHistoryItem[] = rawProjects.map((project) => {
        //         const netlifyCreds = project.connections?.find(
        //           (conn) => conn.connectionType === 'netlify',
        //         )?.connectionData;
        //         return {
        //           projectId: project.projectId,
        //           cloudChatId: project.chatsMetadata[project.chatsMetadata.length - 1]?.chatId,
        //           currentVersion: project.currentVersion,
        //           timestamp: project.lastOpenedOn.toISOString(),
        //           cacheTime: project.cacheTime?.toISOString() || '',
        //           techStack: {
        //             framework: `${project.techStack.framework}_${project.techStack.cssLib}`,
        //             language: project.techStack.language,
        //             packageManager: project.techStack.packageManager,
        //           },
        //           isGitRepo: project.isGitRepo,
        //           initSource: project.initSource,
        //           type: project.type,
        //           idbChatId: project.idbChatId,
        //           projectRules: project.projectRules,
        //           urlId: project.urlId,
        //           projectName: project.projectName,
        //           netlifyCreds: netlifyCreds as NetlifyCreds,
        //           connections: project.connections.reduce((acc, conn) => {
        //             if (conn.connectionType === 'netlify') {
        //               return acc;
        //             }

        //             return {
        //               ...acc,
        //               [conn.connectionType]: conn.connectionData,
        //             };
        //           }, {}),
        //         };
        //       });
        //       console.log('[Root Loader] Fetched projects (deferred):', serialized.length);
        //       return serialized;
        //     } catch (error) {
        //       console.error('[Root Loader] Error fetching projects (deferred): ', error);
        //       return [] as CloudChatHistoryItem[];
        //     }
        //   })();
        // }
      } catch (error) {
        console.error('[Root Loader] Error getting user data: ', error);
        userData = null;
        return redirect('/onboarding');
      }

      // country
      const country = request.headers.get('CF-IPCountry');
      const currency = country ? getCurrencyFromCountry(country) : 'INR';

      return defer({
        baseUrl,
        newDomain,
        paymentUrl,
        currency,
        ENV: {
          SENTRY_DSN: sentryDsn,
          POSTHOG_KEY: posthogKey,
          POSTHOG_HOST: posthogHost,
          EARLY_ACCESS_URL: baseUrl,
          POSTHOG_USE_PROXY: posthogUseProxy,
          VITE_QUESTIONNAIRE_SHEET_ID: questionnaireSheetId,
          VITE_QUESTIONNAIRE_TAB_ID: questionnaireTabId,
          VITE_QUESTIONNAIRE_USERNAME: questionnaireUsername,
        },
        token: checkAuthResponse.token, // External token for API calls
        accessToken: checkAuthResponse.accessToken, // JWT for OTP or OAuth token for Google
        user: {
          id: checkAuthResponse.id,
          email: checkAuthResponse.email,
          name: checkAuthResponse.name,
          provider: checkAuthResponse.provider,
          picture: checkAuthResponse.picture,
        },
        prompt: url.searchParams.get('prompt') || '',
        userData,

        // cloudRevert (burst)
        // projects,
      });
    } else {
      console.error('[Root Loader] Missing required tokens');

      /*
       * console.error('[Root Loader] Access token:', !!checkAuthResponse.accessToken);
       * console.error('[Root Loader] External token:', !!checkAuthResponse.token);
       */
    }
  } else {
    console.log('[Root Loader] No authentication found');
  }

  /* Don't redirect if already on maintenance page */
  if (url.pathname === '/maintenance') {
    console.log('[Root Loader] On maintenance page, skipping redirect');
    return json({});
  }

  console.log('[Root Loader] Redirecting to onboarding');

  if (url.searchParams.get('prompt')) {
    return redirect(`/onboarding?prompt=${url.searchParams.get('prompt')}`);
  } else {
    return redirect('/onboarding');
  }
};

export const clientLoader = async ({ serverLoader }: ClientLoaderFunctionArgs) => {
  const serverData = (await serverLoader()) as ServerLoaderData;

  const migrated = localStorage.getItem('domain-migrated');
  const migrationId = localStorage.getItem('migration-id');
  const originalDomain = window.location.origin;

  /*
   * console.log('[Client Loader] Original domain:', originalDomain);
   * console.log('[Client Loader] New domain:', serverData.newDomain);
   * console.log('[Client Loader] Migrated:', migrated);
   * console.log('[Client Loader] Migration ID:', migrationId);
   */

  /* Redirect to setup if migration ID exists but migration is not completed and user is on new domain */
  if (!migrated && migrationId && originalDomain === serverData.newDomain) {
    return redirect(`/setup?migrate=true&id=${migrationId}`);
  }

  return serverData;
};

/* This tells Remix to run clientLoader on hydration */
clientLoader.hydrate = true;

export const links: LinksFunction = () => [
  /*
   * {
   *   rel: 'icon',
   *   href: '/favicon.svg',
   *   type: 'image/svg+xml',
   * },
   */
  { rel: 'stylesheet', href: reactToastifyStyles },
  { rel: 'stylesheet', href: tailwindReset },
  { rel: 'stylesheet', href: globalStyles },
  { rel: 'stylesheet', href: xtermStyles },
  { rel: 'stylesheet', href: themeVariables },
  { rel: 'stylesheet', href: 'https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&display=swap' },

  // { rel: 'stylesheet', href: independenceDayStyles },

  /*
   * Using local Inter Display fonts defined in app/styles/fonts.scss
   * instead of Google Fonts
   */

  // Preload critical LCP images for better performance
  {
    rel: 'preload',
    href: '/images/LoginBGnew.webp',
    as: 'image',
    type: 'image/webp',
  },
  {
    rel: 'preload',
    href: '/images/FooterBG.webp',
    as: 'image',
    type: 'image/webp',
  },
  {
    rel: 'preload',
    href: '/images/newbg.avif',
    as: 'image',
    type: 'image/webp',
  },
];

const inlineThemeCode = stripIndents`
  setTutorialKitTheme();

  function setTutorialKitTheme() {
    document.querySelector('html')?.setAttribute('data-theme', 'dark');
  }
`;

export const Head = createHead(() => {
  const { ENV, baseUrl } = useLoaderData<ServerLoaderData>();
  const location = useLocation();

  /*
   * Construct full URL for canonical and og:url
   * const currentUrl = typeof window !== 'undefined'
   *   ? window.location.origin + location.pathname + location.search
   *   : baseUrl
   *     ? `${baseUrl}${location.pathname}${location.search}`
   *     : 'https://alpha.dualite.dev';
   */

  const currentUrl = 'https://alpha.dualite.dev';

  // Meta content based on onboarding.tsx
  const siteTitle = 'Idea to App in minutes with Dualite';
  const siteDescription =
    'Dualite is the fastest way for you to turn your ideas into reality. Build web and mobile apps, AI agents, dashboards, and more without any coding.';
  const siteImage = 'https://alpha.dualite.dev/images/ogImage.png';
  const siteKeywords =
    'Dualite, AI app builder, no-code, web development, mobile apps, AI agents, dashboard builder, Figma import, GitHub import, rapid prototyping, app development';

  return (
    <>
      <meta charSet="utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta name="google-site-verification" content="3aKMXs6U8LIR3Up5mEKM3ldwwXxEr1kOORR0tg1ye0E" />

      {/* Primary Meta Tags */}
      <meta name="title" content={siteTitle} />
      <meta name="description" content={siteDescription} />
      <meta name="keywords" content={siteKeywords} />
      <meta name="author" content="Dualite" />
      <meta name="robots" content="index, follow" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={siteTitle} />
      <meta property="og:description" content={siteDescription} />
      <meta property="og:image" content={siteImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Dualite - Build apps without coding" />
      <meta property="og:site_name" content="Dualite" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={siteTitle} />
      <meta name="twitter:description" content={siteDescription} />
      <meta name="twitter:image" content={siteImage} />
      <meta name="twitter:image:alt" content="Dualite - Build apps without coding" />

      {/* Canonical URL */}
      <link rel="canonical" href={currentUrl} />

      {/* Additional Meta Tags */}
      <meta name="theme-color" content="#10110C" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content="Dualite" />

      {/* <script
        dangerouslySetInnerHTML={{
          __html: `!function(){
    var appUrl = "https://app.formbricks.com";
    var environmentId = "cmexx4vpd6s15u5017u4476x2";
    var t=document.createElement("script");t.type="text/javascript",t.async=!0,t.src=appUrl+"/js/formbricks.umd.cjs";var e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(t,e),setTimeout(function(){window.formbricks.setup({environmentId: environmentId, appUrl: appUrl})},500)
}();`,
        }}
      /> */}

      <Meta />
      <Links />

      <script
        dangerouslySetInnerHTML={{
          __html: `window.ENV = ${JSON.stringify(ENV)};`,
        }}
      />
      <script dangerouslySetInnerHTML={{ __html: inlineThemeCode }} />

      {/* Google tag (gtag.js) */}
      <script async src="https://www.googletagmanager.com/gtag/js?id=G-JRRE2F8Z2N"></script>
      <script
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'G-JRRE2F8Z2N');
          `,
        }}
      />
    </>
  );
});

export function Layout({ children }: { children: React.ReactNode }) {
  const theme = useStore(themeStore);
  const error = useRouteError();
  const hasError = error !== null;
  const location = useLocation();
  const loaderData = useLoaderData() as ServerLoaderData;
  const userData = loaderData?.userData;
  usePosthogPageView();
  usePosthogIdentify(loaderData?.user || null);
  useEffect(() => {
    document.querySelector('html')?.setAttribute('data-theme', theme);
  }, [theme]);

  // PostHog SPA pageview tracking on route changes (client-only)
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    (async () => {
      try {
        const { default: posthog } = await import('posthog-js');
        posthog.capture('$pageview');
      } catch {}
    })();
  }, [location.pathname, location.search]);

  // Ribbon only on "/" and "/onboarding" but NOT on chat routes
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : location.pathname;
  const hash = typeof window !== 'undefined' ? window.location.hash : '';
  console.log('[Root Layout] Hash:', hash);

  const isMaintenancePage = currentPath === '/maintenance';

  // If on maintenance page or error page, render minimal layout
  if (isMaintenancePage || hasError) {
    return (
      <>
        {children}
        <ScrollRestoration />
        <Scripts />
      </>
    );
  }

  // Regular layout for all other pages
  const isOnChatRoute = currentPath.startsWith('/chat');
  const showRibbon = (currentPath === '/' || currentPath === '/onboarding') && !isOnChatRoute;

  // Ribbon close state and animation
  const [ribbonVisible, setRibbonVisible] = useState(true);
  const [ribbonClosing, setRibbonClosing] = useState(false);

  // Pro Modal State
  const [isProUpgradeModalOpen, setIsProUpgradeModalOpen] = useState(false);

  const handleCloseRibbon = () => {
    setRibbonClosing(true);
    setTimeout(() => {
      setRibbonVisible(false);
    }, 350); // match animation duration
  };

  const areAllPromptsUsed = userData ? userData.promptsUsed === userData.maxPrompts : false;

  return (
    <div
      style={{
        backgroundColor: '#000000',
        minHeight: '100vh',
        width: '100%',
        maxWidth: '100vw',
        position: 'relative',
        overflowX: 'hidden',
      }}
    >
      {!currentPath.startsWith('/chat') && (
        <LazyBackgroundImage src="/images/newbg.avif" className="absolute inset-0 w-full h-full" />
      )}

      {/* Content container with proper z-index */}
      <div className="relative z-10">
        {/* Indian Independence Day Banner */}
        {/* <div className="independence-day-banner" /> */}

        {/* Safari Block Modal - Shows on all pages for Safari users */}
        <SafariBlockModal />
        {hash.includes('access_token') && <SupabaseAuthSuccessModal isOpen={hash.includes('access_token')} />}

        {showRibbon && ribbonVisible && (
          <ClientOnly>
            {() => (
              <Ribbon2
                isVisible={showRibbon}
                onClose={handleCloseRibbon}
                onClaimClick={() => {
                  if (currentPath === '/onboarding') {
                    const pricingElement = document.getElementById('pricing');

                    if (pricingElement) {
                      pricingElement.scrollIntoView({ behavior: 'smooth' });
                    }
                  } else {
                    setIsProUpgradeModalOpen(true);
                  }
                }}
              />
            )}
          </ClientOnly>
        )}

        {/* Pro Upgrade Modal triggered by Ribbon */}
        <ClientOnly>
          {() => (
            <>
              <ProUpgradeModal
                isOpen={isProUpgradeModalOpen}
                onClose={() => setIsProUpgradeModalOpen(false)}
                isTopUp={userData?.isSubscribed && areAllPromptsUsed}
                paymentUrl={loaderData?.paymentUrl || ''}
              />
            </>
          )}
        </ClientOnly>

        {!hasError && <ClientOnly>{() => <Header paymentUrl={loaderData?.paymentUrl || ''} />}</ClientOnly>}
        {children}
      </div>
      <ScrollRestoration />
      <Scripts />

      {/* Global ToastContainer */}
      <ToastContainer
        icon={({ type }) => {
          switch (type) {
            case 'success': {
              return <div className="i-ph:check-bold text-alpha-elements-icon-success text-2xl" />;
            }
            case 'error': {
              return <div className="i-ph:warning-circle-bold text-alpha-elements-icon-error text-2xl" />;
            }
          }
          return undefined;
        }}
        position="bottom-right"
      />

      {/* Unsplash Attribution - Only show when background image is visible */}
      {/* {!currentPath.startsWith('/chat') && (
        <UnsplashAttribution 
          photographer="Elliott Engelmann"
          photoUrl="https://unsplash.com/photos/silhouette-of-mountain-DjlKxYFJlTc?utm_medium=referral&utm_source=dualite.dev"
        />
      )} */}

      {/* Language Switcher - Bottom Left - Only show on home route */}
      {currentPath === '/' && (
        <div className="fixed bottom-4 left-4 z-50">
          <ClientOnly>{() => <LanguageSwitcher />}</ClientOnly>
        </div>
      )}
    </div>
  );
}

function App() {
  const data = useLoaderData<ServerLoaderData>();

  // console.log('data from app >>>', data);

  // const userId = data?.token;
  const user = data?.userData as UserData | null;
  usePosthogUserProperties();

  // const location = useLocation();

  // cloudRevert
  // const projects = data?.projects as CloudChatHistoryItem[] | Promise<CloudChatHistoryItem[]> | null;

  /* Extract URL parameters for EventStatusProvider (from query string) */
  // const urlParams = new URLSearchParams(location.search);
  // const eventIdsParam = urlParams.get('eventIds');
  // const projectIdQueryParam = urlParams.get('projectId');

  // const chatIdQueryParam = urlParams.get('chatId');

  /*
   * Extract projectId from pathname if on /chat/:projectId route
   * URL pattern: /chat/{projectId}
   * Example: /chat/d743f4ba-4ea1-4ac3-bd7a-8cd599925a55
   */
  // const chatRouteMatch = location.pathname.match(/^\/chat\/([^/]+)\/([^/]+)$/);
  // const projectIdFromPath = chatRouteMatch ? chatRouteMatch[1] : null;
  // const urlIdFromPath = chatRouteMatch ? chatRouteMatch[2] : null;

  /* Priority: Query params > Path params */
  // const projectId = projectIdQueryParam || projectIdFromPath || undefined;

  // console.log('[EventStatusProvider] URL params:', {
  //   pathname: location.pathname,
  //   search: location.search,
  //   projectIdFromQuery: projectIdQueryParam,
  //   projectIdFromPath,
  //   projectId,
  //   userId,
  //   urlIdFromPath,
  // });

  // const [showWelcomeModal, setShowWelcomeModal] = useState(location.pathname === '/');

  useEffect(() => {
    console.log('[App] === App Component Mounted ===');
    console.log('[App] Loader data present:', !!data);

    if (data?.prompt) {
      console.log('[Prompt] Prompt from loader:', data.prompt);
      localStorage.setItem('prompt', data.prompt || '');
    }

    if (user) {
      userStore.set(user);

      /*
       * Check if user was created on August 15th and today is August 15th, 2025
       * if (userData.created_at) {
       *   const createdAt = new Date(userData.created_at);
       *   const now = new Date();
       *   const isAugust15th = createdAt.getMonth() === 7 && createdAt.getDate() === 15; // Month is 0-based, so 7 is August
       *   const isToday15thAugust2025 = now.getMonth() === 7 && now.getDate() === 15 && now.getFullYear() === 2025;
       *   console.log('[App] User created on August 15th:', isAugust15th);
       *   console.log('[App] Today is August 15th, 2025:', isToday15thAugust2025);
       */

      /*
       *   if (isAugust15th && isToday15thAugust2025 && location.pathname === '/') {
       *     setShowWelcomeModal(true);
       *   }
       * }
       */
    }
  }, [user]);

  useEffect(() => {
    if (user && user.email) {
      console.log('[App] Setting Sentry user:', user.email);
      Sentry.setUser({ email: user.email });
    } else {
      console.log('[App] Clearing Sentry user');
      Sentry.setUser(null);
    }
  }, [user?.email]);

  return (
    <>
      {/* <WelcomeModal isOpen={showWelcomeModal} onClose={() => setShowWelcomeModal(false)} /> */}
      {/* Real-time Event Status Provider */}
      {/* cloudRevert */}
      {/* <EventStatusProvider
        projectId={projectId}
        userId={userId}
        autoReconnect={true}
        onConnected={() => console.log('✅ Event status stream connected')}
        onDisconnected={() => console.log('🔌 Event status stream disconnected')}
        onError={(error) => console.error('❌ Event status error:', error)}
      > */}
      {/* Defer projects bootstrapping behind a Suspense boundary */}
      {/* <Suspense fallback={null}>
        <Await resolve={projects} errorElement={<>Error loading projects</>}>
          {(resolvedProjects) => <ProjectsBoot projects={resolvedProjects as ChatHistoryItem[]} />}
        </Await>
      </Suspense> */}
      <ProjectsBoot />
      <Outlet />
      {/* </EventStatusProvider> */}
    </>
  );
}

export default withSentry(App);

function ProjectsBoot() {
  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        if (isMounted) {
          const { projectsFromIDB } = await import('~/lib/stores/storageStatusStore');

          await projectsFromIDB();
        }
      } catch (error) {
        console.error('[ProjectsBoot] Failed to process projects', error);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  return null;
}

/*
 * Error Boundary - Handles 404 and other errors
 */
export function ErrorBoundary() {
  const error = useRouteError();
  console.error(error);

  const is404 = isRouteErrorResponse(error) && error.status === 404;

  captureRemixErrorBoundaryError(error);

  /*
   * Show custom 404 page for 404 errors
   */
  if (is404) {
    return <Custom404 />;
  }

  /*
   * Show generic error page for other errors
   */
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        textAlign: 'center',
        backgroundColor: '#000000',
      }}
    >
      <div
        style={{
          maxWidth: '28rem',
          width: '100%',
          padding: '2rem',
          borderRadius: '0.75rem',
          backgroundColor: 'rgba(20, 20, 24, 0.8)',
          border: '1px solid var(--alpha-border-primary)',
          boxShadow: 'var(--alpha-shadow-card)',
        }}
      >
        <h1
          className="mb-2 text-7xl font-semibold"
          style={{
            color: '#ffffff',
            fontFamily: 'var(--alpha-font-family-display), sans-serif',
          }}
        >
          Oops!
        </h1>

        <h2
          className="mb-6 text-2xl font-medium"
          style={{
            color: '#ffffff',
            fontFamily: 'var(--alpha-font-family-display), sans-serif',
          }}
        >
          Something went wrong
        </h2>

        <p
          className="mb-8"
          style={{
            color: '#e0e0e0',
            fontFamily: 'var(--alpha-font-family-body)',
            fontSize: 'var(--alpha-font-size-base)',
            lineHeight: 'var(--alpha-line-height-relaxed)',
          }}
        >
          {error instanceof Error ? error.message : 'An unexpected error occurred. Please try again later.'}
        </p>

        <a
          href="/"
          className="inline-flex items-center justify-center px-6 py-3 rounded-lg transition-all"
          style={{
            backgroundColor: 'var(--alpha-primary-start)',
            color: '#ffffff',
            fontFamily: 'var(--alpha-font-family-body)',
            fontWeight: 'var(--alpha-font-weight-medium)',
            fontSize: 'var(--alpha-font-size-base)',
            boxShadow: 'var(--alpha-shadow-button)',
          }}
        >
          Back to Home
        </a>
      </div>
    </div>
  );
}
